import type {
  CreateQuestionBody,
  DisplayFilterRule,
  LibraryQuestion,
  LocalizedQuestionText,
  QuestionType,
  RangeLabel,
  SubmissionFilters,
  UpdateQuestionBody,
} from "@/api/survey-library.service";
import { getLocalizedTextDefault } from "@/api/survey-library.service";
import type { EditorialCategory } from "@/api/editorialCategories.service";
import type { Survey, SurveyEditionFields } from "@/api/surveys.service";
import {
  DEFAULT_HEADER_TAG,
  FILTER_KEY_LABELS,
  TYPES_WITH_CHOICES,
  type RuleValueLabelMaps,
} from "./surveyDetails.constants";
import { SURVEY_QUESTION_TYPE_CONFIG, supportsNoExperienceLabel } from "../questionTypes";
import type {
  EditableSurveyState,
  FlattenedQuestion,
  FlattenedSurveyItem,
  HeaderTag,
  SurveyChoiceItem,
  SurveyDefinition,
  SurveyDefinitionElement,
  SurveyItemKind,
} from "./surveyDetails.types";

/**
 * Describes a section that is pinned to one end of the survey's first page.
 * Both the survey header (anchored at start) and footer (anchored at end)
 * are instances of this concept.
 */
export type AnchoredSection = {
  readonly name: string;
  readonly anchorTo: "start" | "end";
};

export const HEADER_SECTION: AnchoredSection = {
  name: "survey_header",
  anchorTo: "start",
};

export const FOOTER_SECTION: AnchoredSection = {
  name: "footer",
  anchorTo: "end",
};

export const ANCHORED_SECTIONS: readonly AnchoredSection[] = [HEADER_SECTION, FOOTER_SECTION];

export function extractSourceTitle(raw: unknown): string | undefined {
  if (typeof raw === "string") return raw || undefined;
  if (raw && typeof raw === "object") {
    const loc = raw as Record<string, string>;
    return loc.default || loc.en || undefined;
  }
  return undefined;
}

export function toSurveyDefinition(definition: object | null | undefined): SurveyDefinition {
  if (!definition || typeof definition !== "object") {
    return { pages: [] };
  }

  const maybePages = (definition as { pages?: unknown }).pages;
  if (!Array.isArray(maybePages)) {
    return { pages: [] };
  }

  const pages = maybePages.map((page, pageIndex) => {
    const pageObject = page as { name?: unknown; elements?: unknown };
    const elements = Array.isArray(pageObject.elements)
      ? pageObject.elements
          .map((element, elementIndex) => {
            const elementObject = element as Record<string, unknown>;
            if (
              !element ||
              typeof element !== "object" ||
              typeof elementObject.type !== "string" ||
              typeof elementObject.name !== "string"
            ) {
              return {
                type: "text",
                name: `question_${pageIndex + 1}_${elementIndex + 1}`,
              };
            }

            return {
              ...elementObject,
              type: elementObject.type,
              name: elementObject.name,
              title: extractSourceTitle(elementObject.title),
              description: extractSourceTitle(elementObject.description),
              html:
                elementObject.type === "html"
                  ? extractSourceTitle(elementObject.html)
                  : elementObject.html,
              isRequired: Boolean(elementObject.isRequired ?? elementObject.required),
            } as SurveyDefinitionElement;
          })
          .filter(Boolean)
      : [];

    return {
      name: typeof pageObject.name === "string" ? pageObject.name : undefined,
      elements,
    };
  });

  return { pages };
}

export function toEditableSurveyState(survey: Survey): EditableSurveyState {
  return {
    name: survey.name,
    edition: survey.edition,
    publication: survey.publication,
    publicationId: survey.publicationId,
    editionId: survey.editionId ?? null,
    version: survey.version,
    lockVersion: survey.lockVersion,
    defaultLanguage: survey.defaultLanguage,
    status: survey.status,
    definition: toSurveyDefinition(survey.definition),
    regionTypes: survey.regionTypes ?? [],
    refereeTypes: survey.refereeTypes ?? [],
    refereeCategories: survey.refereeCategories ?? [],
  };
}

export function toSurveyEditionFields(survey: EditableSurveyState): SurveyEditionFields | null {
  if (survey.editionId) {
    return {
      editionId: survey.editionId,
      ...(survey.publicationId ? { publicationId: survey.publicationId } : {}),
    };
  }
  if (!survey.edition || !survey.publicationId) return null;
  return {
    edition: survey.edition,
    publication: survey.publication,
    publicationId: survey.publicationId,
  };
}

export function isFooterElement(element: SurveyDefinitionElement): boolean {
  return isSectionElement(element, FOOTER_SECTION);
}

export function isHeaderElement(element: SurveyDefinitionElement): boolean {
  return isSectionElement(element, HEADER_SECTION);
}

export function ensureFooterAtEnd(definition: SurveyDefinition): SurveyDefinition {
  return ensureSectionAnchored(definition, FOOTER_SECTION);
}

export function ensureHeaderAtStart(definition: SurveyDefinition): SurveyDefinition {
  return ensureSectionAnchored(definition, HEADER_SECTION);
}

export function prepareDefinitionForSave(definition: SurveyDefinition): SurveyDefinition {
  let result = definition;
  result = ensureSectionAnchored(result, HEADER_SECTION);
  result = ensureSectionAnchored(result, FOOTER_SECTION);
  return result;
}

export function flattenQuestions(definition: SurveyDefinition): FlattenedQuestion[] {
  const flat: FlattenedQuestion[] = [];
  definition.pages.forEach((page, pageIndex) => {
    page.elements.forEach((element, elementIndex) => {
      if (isAnyAnchoredSectionElement(element)) return;
      flat.push({ element, order: flat.length, pageIndex, elementIndex });
    });
  });
  return flat;
}

export function inferItemKind(element: SurveyDefinitionElement): SurveyItemKind {
  if (element.type !== "html") {
    return "question";
  }

  const htmlValue = typeof element.html === "string" ? element.html.trim().toLowerCase() : "";
  if (htmlValue.startsWith("<h1") || htmlValue.startsWith("<h2") || htmlValue.startsWith("<h3")) {
    return "header";
  }
  // A divider is a native SurveyJS `html` element whose markup is just an <hr>.
  // This branch MUST precede the freeText fallthrough, otherwise the divider
  // would be misclassified as free text.
  if (htmlValue.startsWith("<hr")) {
    return "divider";
  }
  return "freeText";
}

export function flattenSurveyItems(definition: SurveyDefinition): FlattenedSurveyItem[] {
  return flattenQuestions(definition).map(item => ({ ...item, kind: inferItemKind(item.element) }));
}

export function ruleUsesPublicationScopedTaxonomy(rule: DisplayFilterRule): boolean {
  const filters = rule.submissionFilters;
  if (!filters) return false;
  return Boolean(filters.region || filters.practiceGroup || filters.practiceArea);
}

export function definitionHasPublicationScopedTaxonomy(definition: SurveyDefinition): boolean {
  for (const page of definition.pages) {
    for (const element of page.elements) {
      const rules = element.displayRules;
      if (!Array.isArray(rules)) continue;
      if (rules.some(ruleUsesPublicationScopedTaxonomy)) return true;
    }
  }
  return false;
}

export function countTestimonialQuestions(definition: SurveyDefinition): number {
  let count = 0;
  for (const { element } of flattenQuestions(definition)) {
    if (inferItemKind(element) !== "question") continue;
    if (element.type !== "comment" && element.type !== "multi-testimonial") continue;
    const stored = element.libraryQuestionType;
    if (stored === "testimonial" || stored === "multi-testimonial") {
      count += 1;
      continue;
    }
    if (!stored) {
      count += 1;
    }
  }
  return count;
}

/**
 * Maximum length for the slug basis of a question's internal `name`.
 * The `name` is just an internal SurveyJS response key — it must stay unique and
 * readable, but never needs to mirror the full (potentially very long) title.
 */
export const MAX_QUESTION_NAME_SLUG_LENGTH = 40;

export function slugifyQuestionName(value: string): string {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (slug.length > MAX_QUESTION_NAME_SLUG_LENGTH) {
    // Cut to the last word (underscore) boundary at or before the cap so we
    // never split a word in half; fall back to a hard cut when the first word
    // alone already exceeds the cap. Strip any trailing underscore.
    const capped = slug.slice(0, MAX_QUESTION_NAME_SLUG_LENGTH);
    const lastBoundary = capped.lastIndexOf("_");
    const truncated = (lastBoundary > 0 ? capped.slice(0, lastBoundary) : capped).replace(
      /_+$/,
      "",
    );
    return truncated || "question";
  }

  return slug || "question";
}

/**
 * Length of the random alphanumeric suffix appended to every generated question
 * `name`. A short suffix (5 chars from [a-z0-9]) is enough to make duplicate
 * internalLabels collision-resistant while keeping the name readable.
 */
export const QUESTION_NAME_SUFFIX_LENGTH = 5;

/** Max attempts to regenerate a colliding random suffix before falling back. */
const MAX_SUFFIX_ATTEMPTS = 10;

const SUFFIX_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

/**
 * Generates a short random lowercase-alphanumeric suffix. Math.random is fine
 * here: this is app runtime code and the suffix only needs to be unique within a
 * single survey definition, not cryptographically secure.
 */
export function generateQuestionNameSuffix(): string {
  let suffix = "";
  for (let i = 0; i < QUESTION_NAME_SUFFIX_LENGTH; i++) {
    suffix += SUFFIX_ALPHABET[Math.floor(Math.random() * SUFFIX_ALPHABET.length)];
  }
  return suffix;
}

export function createUniqueQuestionName(definition: SurveyDefinition, title: string): string {
  const usedNames = new Set(flattenQuestions(definition).map(question => question.element.name));
  const base = slugifyQuestionName(title);

  // Prefer the clean base slug: when it is free, the `name` mirrors the
  // (slugified, capped) title with no noise appended.
  if (!usedNames.has(base)) {
    return base;
  }

  // Collision-resolution fallback: the base is already taken, so append a random
  // suffix to make the new entry unique. Retry on the (extremely unlikely) event
  // that the suffixed candidate also collides.
  for (let attempt = 0; attempt < MAX_SUFFIX_ATTEMPTS; attempt++) {
    const candidate = `${base}_${generateQuestionNameSuffix()}`;
    if (!usedNames.has(candidate)) {
      return candidate;
    }
  }

  // Safety fallback: if every random suffix collided (extremely unlikely), walk a
  // numeric counter so we never return a duplicate name.
  let count = 2;
  let candidate = `${base}_${count}`;
  while (usedNames.has(candidate)) {
    count += 1;
    candidate = `${base}_${count}`;
  }
  return candidate;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function isHeaderTag(value: string): value is HeaderTag {
  return value === "h1" || value === "h2" || value === "h3";
}

export function parseHeaderTagFromHtml(html: string | undefined): HeaderTag {
  if (!html) {
    return DEFAULT_HEADER_TAG as HeaderTag;
  }

  const matchedTag = html
    .trim()
    .match(/^<\s*(h[1-3])\b/i)?.[1]
    ?.toLowerCase();
  return matchedTag && isHeaderTag(matchedTag) ? matchedTag : (DEFAULT_HEADER_TAG as HeaderTag);
}

export function htmlToPlainText(
  html: string | undefined,
  options: { trim?: boolean } = {},
): string {
  if (!html) {
    return "";
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(html, "text/html");
  const text = parsedDocument.body.textContent ?? "";
  return options.trim === false ? text : text.trim();
}

export function getItemKindLabel(item: FlattenedSurveyItem): string {
  if (item.kind === "question") {
    return "Question";
  }

  if (item.kind === "header") {
    const htmlValue = typeof item.element.html === "string" ? item.element.html : undefined;
    return `Header ${parseHeaderTagFromHtml(htmlValue).toUpperCase()}`;
  }

  if (item.kind === "divider") {
    return "Divider";
  }

  return "Free Text";
}

export function getSurveyQuestionTypeLabel(element: {
  type: string;
  inputType?: unknown;
  libraryQuestionType?: QuestionType;
  rateMax?: number;
}): string {
  if (element.type === "range") {
    const fromLibrary = element.libraryQuestionType;
    if (fromLibrary === "range-10" || fromLibrary === "range-100") {
      return fromLibrary === "range-10" ? "Range 10" : "Range 100";
    }
    const max = typeof element.rateMax === "number" ? element.rateMax : undefined;
    if (max === 10) {
      return "Range 10";
    }
    if (max === 100) {
      return "Range 100";
    }
    return "Range";
  }

  if (element.type === "comment") {
    if (element.libraryQuestionType === "testimonial") {
      return "Testimonial";
    }
    if (element.libraryQuestionType === "multi-testimonial") {
      return "Multi testimonial";
    }
  }

  if (element.type === "multi-testimonial") {
    return "Multi testimonial";
  }

  if (element.type === "comment") {
    if (element.libraryQuestionType === "testimonial") {
      return "Testimonial";
    }
    if (element.libraryQuestionType === "multi-testimonial") {
      return "Multi testimonial";
    }
  }

  const labels: Record<string, string> = {
    text: "Text",
    comment: "Long text",
    dropdown: "Dropdown",
    radiogroup: "Single choice",
    boolean: "Yes / No",
  };

  return labels[element.type] ?? element.type;
}

export function getLanguageFlag(languageCode: string): string {
  const flags: Record<string, string> = {
    en: "🇬🇧",
    fr: "🇫🇷",
    de: "🇩🇪",
    es: "🇪🇸",
    it: "🇮🇹",
    zh: "🇨🇳",
    pl: "🇵🇱",
    ja: "🇯🇵",
    pt: "🇵🇹",
    nl: "🇳🇱",
  };

  return flags[languageCode] ?? "🏳️";
}

export function getItemSourceText(item: FlattenedSurveyItem): string {
  if (item.kind === "question") {
    return extractSourceTitle(item.element.title) ?? item.element.name;
  }

  // A divider carries no user text, so it has no translatable source text.
  if (item.kind === "divider") {
    return "";
  }

  if (item.kind === "header" || item.kind === "freeText") {
    const htmlValue = typeof item.element.html === "string" ? item.element.html : "";
    const parsedText = htmlToPlainText(htmlValue);
    return extractSourceTitle(item.element.title) ?? parsedText ?? item.element.name;
  }

  return item.element.name;
}

export function getTranslationItemKey(item: FlattenedSurveyItem): string {
  return `${item.element.name}::${item.order}`;
}

export function getChoiceValue(choice: SurveyChoiceItem): string {
  return typeof choice === "string" ? choice : choice.value;
}

export function getChoiceSourceText(choice: SurveyChoiceItem): string {
  if (typeof choice === "string") return choice;
  return extractSourceTitle(choice.text) ?? choice.value;
}

export function getChoiceTranslationKey(item: FlattenedSurveyItem, choiceIndex: number): string {
  return `${getTranslationItemKey(item)}::c${choiceIndex}`;
}

export function getRangeLabelTranslationKey(item: FlattenedSurveyItem, labelId: string): string {
  return `${getTranslationItemKey(item)}::rl::${labelId}`;
}

export function getDescriptionTranslationKey(item: FlattenedSurveyItem): string {
  return `${getTranslationItemKey(item)}::desc`;
}

export function getDescriptionSourceText(element: SurveyDefinitionElement): string {
  const d = element.description;
  if (d === undefined || d === null) return "";
  if (typeof d === "string") return d.trim();
  return getLocalizedTextDefault(d as LocalizedQuestionText).trim();
}

export function hasTranslatableQuestionDescription(item: FlattenedSurveyItem): boolean {
  return item.kind === "question" && getDescriptionSourceText(item.element).length > 0;
}

export type LocalizedLabelFieldKey = "subjectLabel" | "noExperienceLabel";

export type LocalizedLabelField = {
  key: LocalizedLabelFieldKey;
  displayName: string;
  editorIdPrefix: string;
  appliesTo: (element: SurveyDefinitionElement) => boolean;
};

export const SUBJECT_LABEL_FIELD: LocalizedLabelField = {
  key: "subjectLabel",
  displayName: "Subject label",
  editorIdPrefix: "subject-label-editor",
  appliesTo: element => element.type === "multi-testimonial",
};

export const NO_EXPERIENCE_LABEL_FIELD: LocalizedLabelField = {
  key: "noExperienceLabel",
  displayName: '"No experience" label',
  editorIdPrefix: "no-experience-label-editor",
  appliesTo: element =>
    element.type === "range" &&
    element.libraryQuestionType !== undefined &&
    supportsNoExperienceLabel(element.libraryQuestionType),
};

export const LOCALIZED_LABEL_FIELDS: readonly LocalizedLabelField[] = [
  SUBJECT_LABEL_FIELD,
  NO_EXPERIENCE_LABEL_FIELD,
];

export function getLabelFieldTranslationKey(
  item: FlattenedSurveyItem,
  field: LocalizedLabelField,
): string {
  return `${getTranslationItemKey(item)}::${field.key}`;
}

export function getLabelFieldSourceText(
  element: SurveyDefinitionElement,
  field: LocalizedLabelField,
): string {
  return getLocalizedTextDefault(element[field.key] ?? null).trim();
}

export function hasTranslatableLabelField(
  item: FlattenedSurveyItem,
  field: LocalizedLabelField,
): boolean {
  return (
    item.kind === "question" &&
    field.appliesTo(item.element) &&
    getLabelFieldSourceText(item.element, field).length > 0
  );
}

export function getTranslatableLabelFields(item: FlattenedSurveyItem): LocalizedLabelField[] {
  return LOCALIZED_LABEL_FIELDS.filter(field => hasTranslatableLabelField(item, field));
}

export function getRangeLabelSourceText(label: {
  label?: LocalizedQuestionText | string | Record<string, string> | unknown;
}): string {
  if (label.label === undefined || label.label === null) return "";
  if (typeof label.label === "string") return label.label;
  if (typeof label.label === "object") {
    return getLocalizedTextDefault(label.label as LocalizedQuestionText);
  }
  return "";
}

export function hasTranslatableRangeLabels(element: SurveyDefinitionElement): boolean {
  if (element.libraryQuestionType === "range-10") return false;
  return (
    element.type === "range" &&
    Array.isArray(element.rangeLabels) &&
    element.rangeLabels.some(rl => getRangeLabelSourceText(rl).trim().length > 0)
  );
}

export function hasTranslatableChoices(element: SurveyDefinitionElement): boolean {
  return Array.isArray(element.choices) && element.choices.length > 0 && element.type !== "boolean";
}

export function buildContentElement(
  definition: SurveyDefinition,
  kind: Extract<SurveyItemKind, "header" | "freeText">,
  text: string,
  headerTag: HeaderTag = DEFAULT_HEADER_TAG as HeaderTag,
): SurveyDefinitionElement {
  const trimmedText = text.trim();
  const fallback = kind === "header" ? "Section header" : "Free text block";
  const contentText = trimmedText || fallback;
  const namePrefix = kind === "header" ? "header" : "free_text";
  const name = createUniqueQuestionName(definition, `${namePrefix}_${contentText}`);
  const escapedContent = escapeHtml(contentText);

  return {
    type: "html",
    name,
    title: contentText,
    html:
      kind === "header"
        ? `<${headerTag}>${escapedContent}</${headerTag}>`
        : `<p>${escapedContent}</p>`,
  };
}

/**
 * Builds a divider element: a native SurveyJS `html` element whose markup is a
 * bare `<hr />`. A divider has no user text (no title, no escaping) — it is
 * purely visual and renders as a horizontal rule between questions.
 */
export function buildDividerElement(definition: SurveyDefinition): SurveyDefinitionElement {
  return {
    type: "html",
    name: createUniqueQuestionName(definition, "divider"),
    // Inline margin so the ~20px vertical spacing travels with the definition and
    // renders in both the SurveyJS preview and the public form without touching
    // the shared renderer package.
    html: '<hr style="margin-top:20px;margin-bottom:20px;" />',
  };
}

/**
 * Appends an element to the survey's first page (creating the page when the
 * definition is empty). Shared by every "add" path so the page-0 insertion
 * logic lives in one place instead of being duplicated at each call site.
 */
export function appendElementToFirstPage(
  definition: SurveyDefinition,
  element: SurveyDefinitionElement,
): SurveyDefinition {
  const pages =
    definition.pages.length === 0
      ? [{ name: "page1", elements: [element] }]
      : definition.pages.map((page, pageIndex) =>
          pageIndex === 0 ? { ...page, elements: [...page.elements, element] } : page,
        );
  return { pages };
}

export function getFooterText(definition: SurveyDefinition): string {
  return getSectionText(definition, FOOTER_SECTION);
}

export function getHeaderText(definition: SurveyDefinition): string {
  return getSectionText(definition, HEADER_SECTION);
}

export function removeFooter(definition: SurveyDefinition): SurveyDefinition {
  return removeSection(definition, FOOTER_SECTION);
}

export function removeHeader(definition: SurveyDefinition): SurveyDefinition {
  return removeSection(definition, HEADER_SECTION);
}

export function setFooterText(definition: SurveyDefinition, text: string): SurveyDefinition {
  return setSectionText(definition, FOOTER_SECTION, text);
}

export function setHeaderText(definition: SurveyDefinition, text: string): SurveyDefinition {
  return setSectionText(definition, HEADER_SECTION, text);
}

export function getFooterSourceText(definition: SurveyDefinition): string {
  return getSectionSourceText(definition, FOOTER_SECTION);
}

export function getHeaderSourceText(definition: SurveyDefinition): string {
  return getSectionSourceText(definition, HEADER_SECTION);
}

export function extractFooterTranslationsFromRawDefinition(
  rawDefinition: object | null | undefined,
  supportedLanguages: string[],
): Record<string, { value: string; sourceSnapshot: string }> {
  return extractSectionTranslationsFromRawDefinition(
    rawDefinition,
    FOOTER_SECTION,
    supportedLanguages,
  );
}

export function extractHeaderTranslationsFromRawDefinition(
  rawDefinition: object | null | undefined,
  supportedLanguages: string[],
): Record<string, { value: string; sourceSnapshot: string }> {
  return extractSectionTranslationsFromRawDefinition(
    rawDefinition,
    HEADER_SECTION,
    supportedLanguages,
  );
}

export function mergeFooterTranslationsIntoDefinition(
  definition: SurveyDefinition,
  footerTranslations: Record<string, { value: string; sourceSnapshot: string }>,
): SurveyDefinition {
  return mergeSectionTranslationsIntoDefinition(definition, FOOTER_SECTION, footerTranslations);
}

export function mergeHeaderTranslationsIntoDefinition(
  definition: SurveyDefinition,
  headerTranslations: Record<string, { value: string; sourceSnapshot: string }>,
): SurveyDefinition {
  return mergeSectionTranslationsIntoDefinition(definition, HEADER_SECTION, headerTranslations);
}

export function isFooterFullyTranslated(
  definition: SurveyDefinition,
  footerTranslations: Record<string, { value: string; sourceSnapshot: string }>,
  translatableLanguages: string[],
): boolean {
  return isSectionFullyTranslated(
    definition,
    FOOTER_SECTION,
    footerTranslations,
    translatableLanguages,
  );
}

export function isHeaderFullyTranslated(
  definition: SurveyDefinition,
  headerTranslations: Record<string, { value: string; sourceSnapshot: string }>,
  translatableLanguages: string[],
): boolean {
  return isSectionFullyTranslated(
    definition,
    HEADER_SECTION,
    headerTranslations,
    translatableLanguages,
  );
}

// =============================================================================
// Generic anchored-section helpers
// Shared implementation for survey header (anchored at start) and footer (end).
// =============================================================================

export function isSectionElement(
  element: SurveyDefinitionElement,
  section: AnchoredSection,
): boolean {
  return element.name === section.name;
}

export function isAnyAnchoredSectionElement(element: SurveyDefinitionElement): boolean {
  return ANCHORED_SECTIONS.some(section => isSectionElement(element, section));
}

export function getSectionText(definition: SurveyDefinition, section: AnchoredSection): string {
  for (const page of definition.pages) {
    for (const element of page.elements) {
      if (isSectionElement(element, section)) {
        return htmlToPlainText(typeof element.html === "string" ? element.html : undefined, {
          trim: false,
        });
      }
    }
  }
  return "";
}

export function getSectionSourceText(
  definition: SurveyDefinition,
  section: AnchoredSection,
): string {
  return getSectionText(definition, section);
}

export function removeSection(
  definition: SurveyDefinition,
  section: AnchoredSection,
): SurveyDefinition {
  return {
    ...definition,
    pages: definition.pages.map(page => ({
      ...page,
      elements: page.elements.filter(element => !isSectionElement(element, section)),
    })),
  };
}

export function setSectionText(
  definition: SurveyDefinition,
  section: AnchoredSection,
  text: string,
): SurveyDefinition {
  const withoutSection = removeSection(definition, section);

  if (text.trim().length === 0) {
    return withoutSection;
  }

  const newElement: SurveyDefinitionElement = {
    type: "html",
    name: section.name,
    html: `<p>${escapeHtml(text)}</p>`,
  };

  const pages =
    withoutSection.pages.length > 0 ? withoutSection.pages : [{ name: "page1", elements: [] }];

  const firstPage = pages[0];
  const nextElements =
    section.anchorTo === "start"
      ? [newElement, ...firstPage.elements]
      : [...firstPage.elements, newElement];

  return {
    ...withoutSection,
    pages: [{ ...firstPage, elements: nextElements }, ...pages.slice(1)],
  };
}

export function ensureSectionAnchored(
  definition: SurveyDefinition,
  section: AnchoredSection,
): SurveyDefinition {
  let sectionElement: SurveyDefinitionElement | null = null;
  for (const page of definition.pages) {
    for (const element of page.elements) {
      if (isSectionElement(element, section)) {
        sectionElement = element;
        break;
      }
    }
    if (sectionElement) break;
  }

  if (!sectionElement) return definition;

  const withoutSection = removeSection(definition, section);
  const pages =
    withoutSection.pages.length === 0 ? [{ name: "page1", elements: [] }] : withoutSection.pages;

  const firstPage = pages[0];
  const nextElements =
    section.anchorTo === "start"
      ? [sectionElement, ...firstPage.elements]
      : [...firstPage.elements, sectionElement];

  return {
    ...withoutSection,
    pages: [{ ...firstPage, elements: nextElements }, ...pages.slice(1)],
  };
}

export function extractSectionTranslationsFromRawDefinition(
  rawDefinition: object | null | undefined,
  section: AnchoredSection,
  supportedLanguages: string[],
): Record<string, { value: string; sourceSnapshot: string }> {
  const result: Record<string, { value: string; sourceSnapshot: string }> = {};
  if (!rawDefinition || typeof rawDefinition !== "object") return result;

  const pages = (rawDefinition as { pages?: unknown }).pages;
  if (!Array.isArray(pages)) return result;

  let target: Record<string, unknown> | null = null;
  for (const page of pages) {
    const elements = (page as { elements?: unknown }).elements;
    if (!Array.isArray(elements)) continue;
    const found = elements.find(
      (el): el is Record<string, unknown> =>
        Boolean(el) && typeof el === "object" && (el as { name?: unknown }).name === section.name,
    );
    if (found) {
      target = found;
      break;
    }
  }
  if (!target) return result;

  const html = target.html;
  if (!html || typeof html !== "object") return result;

  const localized = html as Record<string, unknown>;
  const defaultHtml = typeof localized.default === "string" ? localized.default : "";
  const sourceSnapshot = htmlToPlainText(defaultHtml, { trim: false });

  for (const lang of supportedLanguages) {
    const raw = localized[lang];
    if (typeof raw === "string" && raw.length > 0) {
      result[lang] = {
        value: htmlToPlainText(raw, { trim: false }),
        sourceSnapshot,
      };
    }
  }
  return result;
}

export function mergeSectionTranslationsIntoDefinition(
  definition: SurveyDefinition,
  section: AnchoredSection,
  sectionTranslations: Record<string, { value: string; sourceSnapshot: string }>,
): SurveyDefinition {
  let targetPageIndex = -1;
  let targetElementIndex = -1;
  let targetElement: SurveyDefinitionElement | null = null;

  for (let p = 0; p < definition.pages.length; p++) {
    const page = definition.pages[p];
    for (let e = 0; e < page.elements.length; e++) {
      if (isSectionElement(page.elements[e], section)) {
        targetPageIndex = p;
        targetElementIndex = e;
        targetElement = page.elements[e];
        break;
      }
    }
    if (targetElement) break;
  }

  if (!targetElement) return definition;

  const defaultHtml = typeof targetElement.html === "string" ? targetElement.html : "";
  const localized: Record<string, string> = { default: defaultHtml };
  for (const [lang, entry] of Object.entries(sectionTranslations)) {
    if (!entry.value.trim()) continue;
    localized[lang] = `<p>${escapeHtml(entry.value)}</p>`;
  }

  return {
    ...definition,
    pages: definition.pages.map((page, p) => {
      if (p !== targetPageIndex) return page;
      return {
        ...page,
        elements: page.elements.map((element, e) => {
          if (e !== targetElementIndex) return element;
          return { ...element, html: localized };
        }),
      };
    }),
  };
}

export function isSectionFullyTranslated(
  definition: SurveyDefinition,
  section: AnchoredSection,
  sectionTranslations: Record<string, { value: string; sourceSnapshot: string }>,
  translatableLanguages: string[],
): boolean {
  const sourceText = getSectionSourceText(definition, section);
  if (sourceText.trim().length === 0) return true;

  for (const lang of translatableLanguages) {
    const entry = sectionTranslations[lang];
    if (!entry || !entry.value.trim()) return false;
    if (entry.sourceSnapshot !== sourceText) return false;
  }
  return true;
}

export function getSectionTranslationStatus(
  definition: SurveyDefinition,
  section: AnchoredSection,
  sectionTranslations: Record<string, { value: string; sourceSnapshot: string }>,
  translatableLanguages: string[],
): "translated" | "outdated" | "missing" {
  const sourceText = getSectionSourceText(definition, section);
  if (sourceText.trim().length === 0) return "translated";

  let hasAnyValue = false;
  for (const lang of translatableLanguages) {
    const entry = sectionTranslations[lang];
    if (!entry || !entry.value.trim()) return "missing";
    hasAnyValue = true;
    if (entry.sourceSnapshot !== sourceText) {
      // Check remaining langs for missing before deciding outdated
      for (const remaining of translatableLanguages) {
        const re = sectionTranslations[remaining];
        if (!re || !re.value.trim()) return "missing";
      }
      return "outdated";
    }
  }
  return hasAnyValue ? "translated" : "missing";
}

export function getItemTranslationStatus(
  item: FlattenedSurveyItem,
  translationsByLanguage: Record<string, Record<string, { value: string; sourceSnapshot: string }>>,
  translatableLanguages: string[],
  getSourceText: (item: FlattenedSurveyItem) => string,
  getKey: (item: FlattenedSurveyItem) => string,
): "translated" | "outdated" | "missing" {
  const sourceText = getSourceText(item);
  const key = getKey(item);
  let hasStaleSnapshot = false;

  const checks = [
    { key, sourceText },
    ...getTranslatableLabelFields(item).map(field => ({
      key: getLabelFieldTranslationKey(item, field),
      sourceText: getLabelFieldSourceText(item.element, field),
    })),
  ];

  for (const check of checks) {
    for (const lang of translatableLanguages) {
      const entry = translationsByLanguage[lang]?.[check.key];
      if (!entry || !entry.value.trim()) return "missing";
      if (entry.sourceSnapshot !== check.sourceText) hasStaleSnapshot = true;
    }
  }

  return hasStaleSnapshot ? "outdated" : "translated";
}

export function mapLibraryTypeToSurveyType(type: LibraryQuestion["type"]): string {
  const typeMap: Record<LibraryQuestion["type"], string> = {
    text: "text",
    choice: "radiogroup",
    dropdown: "dropdown",
    "range-10": "range",
    "range-100": "range",
    boolean: "boolean",
    testimonial: "comment",
    "multi-testimonial": "multi-testimonial",
  };

  return typeMap[type];
}

export function mapSurveyTypeToLibraryType(type: string): QuestionType {
  const typeMap: Record<string, QuestionType> = {
    range: "range-10",
    text: "text",
    radiogroup: "choice",
    dropdown: "dropdown",
    boolean: "boolean",
    comment: "testimonial",
    "multi-testimonial": "multi-testimonial",
  };

  return typeMap[type] ?? "text";
}

export function resolveLibraryQuestionTypeFromElement(
  element: SurveyDefinitionElement,
): QuestionType {
  const stored = element.libraryQuestionType;
  if (typeof stored === "string" && stored.length > 0) {
    return stored;
  }
  return mapSurveyTypeToLibraryType(element.type);
}

export function buildTaxonomyPracticeLabel(category: EditorialCategory): string {
  const parts: string[] = [];
  if (category.practiceGroup) {
    parts.push(category.practiceGroup);
  }
  if (category.practiceArea) {
    parts.push(category.practiceArea);
  }

  const deduped = parts.filter((part, index) => index === 0 || part !== parts[index - 1]);

  if (deduped.length === 0) {
    return category.tableName ?? category.subtype ?? `Practice ${category.id}`;
  }
  if (deduped.length === 1) {
    return deduped[0];
  }
  return deduped.join(" > ");
}

export function formatDisplayRule(
  rule: DisplayFilterRule,
  valueLabelMaps?: RuleValueLabelMaps,
): { label: string; value: string }[] {
  const filters = rule.submissionFilters;
  if (!filters) return [];

  return Object.entries(filters)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => ({
      label: FILTER_KEY_LABELS[key] ?? key,
      value: valueLabelMaps?.[key as keyof SubmissionFilters]?.get(String(value)) ?? String(value),
    }));
}

function localizedQuestionFieldForSurvey(
  value: LocalizedQuestionText | string | null | undefined,
): LocalizedQuestionText | string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") {
    const t = value.trim();
    return t || undefined;
  }
  const cleaned: Record<string, string> = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "string" && v.trim()) cleaned[k] = v.trim();
  }
  if (Object.keys(cleaned).length === 0) return undefined;
  return cleaned as LocalizedQuestionText;
}

function normalizeBooleanDefaultForSurvey(
  rawDefault: string | null | undefined,
): boolean | string | undefined {
  const trimmed = rawDefault?.trim();
  if (!trimmed) return undefined;

  const normalized = trimmed.toLowerCase();
  if (["yes", "true", "1"].includes(normalized)) return true;
  if (["no", "false", "0"].includes(normalized)) return false;
  return trimmed;
}

function normalizeBooleanDefaultForLibrary(
  rawDefault: unknown,
  element: SurveyDefinitionElement,
): string | null {
  if (typeof rawDefault === "boolean") {
    const valueTrue = typeof element.valueTrue === "string" ? element.valueTrue.trim() : "";
    const valueFalse = typeof element.valueFalse === "string" ? element.valueFalse.trim() : "";
    if (rawDefault && valueTrue) return valueTrue;
    if (!rawDefault && valueFalse) return valueFalse;
    return rawDefault ? "Yes" : "No";
  }

  if (typeof rawDefault === "string") {
    const trimmed = rawDefault.trim();
    if (!trimmed) return null;
    const normalized = trimmed.toLowerCase();
    if (["true", "1"].includes(normalized)) return "Yes";
    if (["false", "0"].includes(normalized)) return "No";
    return trimmed;
  }

  return null;
}

export function buildSurveyElementBase(
  question: CreateQuestionBody | UpdateQuestionBody,
): Pick<
  SurveyDefinitionElement,
  | "type"
  | "title"
  | "isRequired"
  | "internalLabel"
  | "defaultValue"
  | "description"
  | "choices"
  | "rateMin"
  | "rateMax"
  | "noExperienceLabel"
> {
  const surveyType = mapLibraryTypeToSurveyType(question.type);
  const hasChoices = TYPES_WITH_CHOICES.has(surveyType);
  const rangeMax = SURVEY_QUESTION_TYPE_CONFIG[question.type]?.rangeMax ?? null;
  // `range-10` renders as a numeric radio group with no per-step captions, so
  // we never persist `rangeLabels` for that type — even if the payload still
  // carries some (e.g. legacy data round-tripped through the editor).
  const supportsRangeLabels = question.type === "range-100";
  const normalizedRangeLabels =
    supportsRangeLabels && rangeMax !== null && Array.isArray(question.rangeLabels)
      ? [...question.rangeLabels]
          .filter(rl => getLocalizedTextDefault(rl.label).trim().length > 0)
          .sort((a, b) => a.order - b.order)
          .map((rl, index) => ({ ...rl, order: index }))
      : null;

  return {
    type: surveyType,
    title:
      localizedQuestionFieldForSurvey(question.text) ??
      getLocalizedTextDefault(question.text).trim(),
    isRequired: Boolean(question.required),
    internalLabel: question.internalLabel?.trim() || undefined,
    defaultValue:
      question.type === "boolean"
        ? normalizeBooleanDefaultForSurvey(question.defaultValue)
        : question.defaultValue?.trim() || undefined,
    description: localizedQuestionFieldForSurvey(question.description),
    choices:
      hasChoices && Array.isArray(question.options) ? question.options.filter(Boolean) : undefined,
    noExperienceLabel:
      supportsNoExperienceLabel(question.type) && question.noExperienceLabel
        ? question.noExperienceLabel
        : undefined,
    ...(rangeMax !== null && {
      rateMin: 0,
      rateMax: rangeMax,
      ...(normalizedRangeLabels &&
        normalizedRangeLabels.length > 0 && { rangeLabels: normalizedRangeLabels }),
    }),
  };
}

function localizedQuestionTextFromUnknown(raw: unknown): LocalizedQuestionText {
  if (!raw || typeof raw !== "object") return { default: "" };
  const record = raw as Record<string, string>;
  return {
    default: record.default ?? record.en ?? "",
    ...record,
  };
}

export function mapSurveyElementToLibraryQuestion(
  element: SurveyDefinitionElement,
): LibraryQuestion {
  const libraryType = resolveLibraryQuestionTypeFromElement(element);

  const rangeLabels: RangeLabel[] | null =
    element.type === "range" && Array.isArray(element.rangeLabels)
      ? element.rangeLabels
          .filter((rl): rl is NonNullable<typeof rl> => Boolean(rl && typeof rl.id === "string"))
          .sort((a, b) => a.order - b.order)
          .map(rl => ({
            id: rl.id,
            order: rl.order,
            label: localizedQuestionTextFromUnknown(rl.label),
          }))
      : null;

  return {
    id: String(element.libraryQuestionId ?? element.name),
    text: { default: extractSourceTitle(element.title) ?? element.name },
    type: libraryType,
    required: Boolean(element.isRequired),
    internalLabel: typeof element.internalLabel === "string" ? element.internalLabel : null,
    defaultValue:
      libraryType === "boolean"
        ? normalizeBooleanDefaultForLibrary(element.defaultValue, element)
        : typeof element.defaultValue === "string"
          ? element.defaultValue
          : null,
    description: (() => {
      const d = element.description;
      if (d === undefined || d === null) return null;
      if (typeof d === "string") {
        const t = d.trim();
        return t ? ({ default: t } satisfies LocalizedQuestionText) : null;
      }
      if (typeof d === "object") {
        const localized = localizedQuestionTextFromUnknown(d);
        const hasAny = Object.values(localized).some(v => v.trim().length > 0);
        return hasAny ? localized : null;
      }
      return null;
    })(),
    options: Array.isArray(element.choices)
      ? element.choices.filter(Boolean).map(getChoiceValue)
      : null,
    rangeLabels,
    // Read the per-locale subjectLabel back so editing an existing survey
    // question re-populates the editor; only set for `multi-testimonial`.
    subjectLabel:
      libraryType === "multi-testimonial" &&
      element.subjectLabel &&
      typeof element.subjectLabel === "object"
        ? localizedQuestionTextFromUnknown(element.subjectLabel)
        : null,
    noExperienceLabel:
      supportsNoExperienceLabel(libraryType) &&
      element.noExperienceLabel &&
      typeof element.noExperienceLabel === "object"
        ? localizedQuestionTextFromUnknown(element.noExperienceLabel)
        : null,
    displayRules: Array.isArray(element.displayRules) ? element.displayRules : [],
    isRandom: false,
    createdBy: null,
    updatedBy: null,
    createdAt: "",
    updatedAt: "",
  };
}

export function mapQuestionPayloadToSurveyElement(
  question: CreateQuestionBody | UpdateQuestionBody,
  definition: SurveyDefinition,
  existingName?: string,
): SurveyDefinitionElement {
  const rules =
    Array.isArray(question.displayRules) && question.displayRules.length > 0
      ? question.displayRules.filter(rule => {
          const filters = rule.submissionFilters as SubmissionFilters | undefined;
          return Boolean(filters && Object.values(filters).some(value => Boolean(value)));
        })
      : [];

  // Prefer the (short, human-set) internalLabel as the name basis; fall back to
  // the localized title when no internalLabel is set.
  const nameBasis = question.internalLabel?.trim() || getLocalizedTextDefault(question.text);

  return {
    ...buildSurveyElementBase(question),
    name: existingName ?? createUniqueQuestionName(definition, nameBasis),
    displayRules: rules.length > 0 ? rules : undefined,
    libraryQuestionType: question.type,
    // Only `multi-testimonial` carries a subjectLabel; the renderer reads it from
    // the saved definition element to build the "+ {label}" add button.
    ...(question.type === "multi-testimonial" &&
      question.subjectLabel && { subjectLabel: question.subjectLabel }),
  };
}

export function mapLibraryQuestionToSurveyElement(
  libraryQuestion: LibraryQuestion,
  definition: SurveyDefinition,
): SurveyDefinitionElement {
  const title = getLocalizedTextDefault(libraryQuestion.text).trim() || "Library question";
  // Prefer the (short, human-set) internalLabel as the name basis; fall back to
  // the title when no internalLabel is set.
  const nameBasis = libraryQuestion.internalLabel?.trim() || title;
  const name = createUniqueQuestionName(definition, nameBasis);
  const surveyType = mapLibraryTypeToSurveyType(libraryQuestion.type);
  const choices =
    TYPES_WITH_CHOICES.has(surveyType) && Array.isArray(libraryQuestion.options)
      ? libraryQuestion.options.filter(Boolean)
      : undefined;

  const displayRules = Array.isArray(libraryQuestion.displayRules)
    ? libraryQuestion.displayRules.filter((rule): rule is DisplayFilterRule => {
        const filters = rule?.submissionFilters as SubmissionFilters | undefined;
        return Boolean(filters && Object.values(filters).some(value => Boolean(value)));
      })
    : [];

  const rangeMax = SURVEY_QUESTION_TYPE_CONFIG[libraryQuestion.type]?.rangeMax ?? null;
  // `range-10` is rendered without per-step captions, so we strip any legacy
  // `rangeLabels` from the SurveyJS element entirely (the key is omitted, not
  // set to []) when inserting a `range-10` library question into a survey.
  const supportsRangeLabels = libraryQuestion.type === "range-100";

  const surveyRangeLabels =
    supportsRangeLabels &&
    rangeMax !== null &&
    Array.isArray(libraryQuestion.rangeLabels) &&
    libraryQuestion.rangeLabels.length > 0
      ? [...libraryQuestion.rangeLabels]
          .sort((a, b) => a.order - b.order)
          .map((rl, index) => ({ ...rl, order: index }))
      : undefined;

  return {
    type: surveyType,
    name,
    title: libraryQuestion.text as LocalizedQuestionText,
    isRequired: libraryQuestion.required,
    internalLabel: libraryQuestion.internalLabel ?? undefined,
    description: localizedQuestionFieldForSurvey(libraryQuestion.description),
    choices,
    displayRules: displayRules.length > 0 ? displayRules : undefined,
    libraryQuestionId: libraryQuestion.id,
    libraryQuestionType: libraryQuestion.type,
    // Carry the per-locale subjectLabel onto the saved definition element so the
    // renderer can read it; only meaningful for `multi-testimonial` questions.
    ...(libraryQuestion.type === "multi-testimonial" &&
      libraryQuestion.subjectLabel && { subjectLabel: libraryQuestion.subjectLabel }),
    ...(supportsNoExperienceLabel(libraryQuestion.type) &&
      libraryQuestion.noExperienceLabel && {
        noExperienceLabel: libraryQuestion.noExperienceLabel,
      }),
    ...(rangeMax !== null && {
      rateMin: 0,
      rateMax: rangeMax,
      ...(surveyRangeLabels && surveyRangeLabels.length > 0 && { rangeLabels: surveyRangeLabels }),
    }),
  };
}

function carryOverLocales(previous: unknown, next: unknown): unknown {
  if (!next || !previous || typeof previous !== "object") return next;
  if (typeof next !== "string" && typeof next !== "object") return next;
  const nextLocalized = (
    typeof next === "string" ? { default: next } : next
  ) as LocalizedQuestionText;
  const previousLocalized = previous as LocalizedQuestionText;
  if (
    getLocalizedTextDefault(previousLocalized).trim() !==
    getLocalizedTextDefault(nextLocalized).trim()
  ) {
    return next;
  }
  return { ...previousLocalized, ...nextLocalized };
}

function carryOverChoices(
  previous: SurveyDefinitionElement["choices"],
  next: SurveyDefinitionElement["choices"],
): SurveyDefinitionElement["choices"] {
  if (!Array.isArray(previous) || !Array.isArray(next)) return next;
  return next.map(choice => {
    if (typeof choice !== "string") return choice;
    const match = previous.find(
      candidate => typeof candidate === "object" && getChoiceValue(candidate) === choice,
    );
    return match && getChoiceSourceText(match) === choice ? match : choice;
  });
}

export function mergeEditedQuestionElement(
  previous: SurveyDefinitionElement,
  edited: SurveyDefinitionElement,
): SurveyDefinitionElement {
  const merged: SurveyDefinitionElement = { ...previous, ...edited };
  merged.title = carryOverLocales(previous.title, edited.title) as SurveyDefinitionElement["title"];
  merged.description = carryOverLocales(
    previous.description,
    edited.description,
  ) as SurveyDefinitionElement["description"];
  for (const field of LOCALIZED_LABEL_FIELDS) {
    merged[field.key] = carryOverLocales(previous[field.key], edited[field.key]) as
      | LocalizedQuestionText
      | undefined;
  }
  merged.choices = carryOverChoices(previous.choices, edited.choices);
  if (Array.isArray(edited.rangeLabels) && Array.isArray(previous.rangeLabels)) {
    const previousLabels = previous.rangeLabels;
    merged.rangeLabels = edited.rangeLabels.map(rangeLabel => {
      const match = previousLabels.find(candidate => candidate.id === rangeLabel.id);
      return match
        ? {
            ...rangeLabel,
            label: carryOverLocales(match.label, rangeLabel.label) as LocalizedQuestionText,
          }
        : rangeLabel;
    });
  }
  return merged;
}

export function updateElementAtOrder(
  definition: SurveyDefinition,
  order: number,
  updater: (value: SurveyDefinitionElement) => SurveyDefinitionElement,
): SurveyDefinition {
  const flat = flattenQuestions(definition);
  const target = flat.find(item => item.order === order);
  if (!target) return definition;

  return {
    pages: definition.pages.map((page, pageIndex) => {
      if (pageIndex !== target.pageIndex) return page;
      return {
        ...page,
        elements: page.elements.map((element, elementIndex) => {
          if (elementIndex !== target.elementIndex) return element;
          return updater(element);
        }),
      };
    }),
  };
}

export function removeElementAtOrder(
  definition: SurveyDefinition,
  order: number,
): SurveyDefinition {
  const flat = flattenQuestions(definition);
  const target = flat.find(item => item.order === order);
  if (!target) return definition;

  return {
    pages: definition.pages
      .map((page, pageIndex) => {
        if (pageIndex !== target.pageIndex) return page;
        return {
          ...page,
          elements: page.elements.filter((_, elementIndex) => elementIndex !== target.elementIndex),
        };
      })
      .filter(page => page.elements.length > 0),
  };
}

export function extractTranslationsFromRawDefinition(
  rawDefinition: object,
  flattenedItems: FlattenedSurveyItem[],
  languages: string[],
): Record<string, Record<string, { value: string; sourceSnapshot: string }>> {
  // Build a map from element.name → raw element object from the raw definition
  const rawElements = new Map<string, Record<string, unknown>>();
  const maybeDef = rawDefinition as { pages?: unknown };
  if (Array.isArray(maybeDef.pages)) {
    for (const page of maybeDef.pages) {
      const maybePage = page as { elements?: unknown };
      if (Array.isArray(maybePage.elements)) {
        for (const el of maybePage.elements) {
          const maybeEl = el as Record<string, unknown>;
          if (typeof maybeEl.name === "string") {
            rawElements.set(maybeEl.name, maybeEl);
          }
        }
      }
    }
  }

  const result: Record<string, Record<string, { value: string; sourceSnapshot: string }>> = {};

  for (const item of flattenedItems) {
    const rawEl = rawElements.get(item.element.name);
    if (!rawEl) continue;

    const sourceText = getItemSourceText(item);
    const translationKey = getTranslationItemKey(item);

    if (rawEl.type === "html") {
      const rawHtml = rawEl.html;
      if (!rawHtml || typeof rawHtml !== "object") continue;
      const localizedHtml = rawHtml as Record<string, string>;
      for (const lang of languages) {
        if (lang === "en") continue;
        const translatedHtml = localizedHtml[lang];
        if (!translatedHtml) continue;
        const plainText = htmlToPlainText(translatedHtml);
        if (!plainText) continue;
        if (!result[lang]) result[lang] = {};
        result[lang][translationKey] = { value: plainText, sourceSnapshot: sourceText };
      }
      continue;
    }

    const rawDescForExtract = rawEl.description;
    if (item.kind === "question" && rawDescForExtract && typeof rawDescForExtract === "object") {
      const localizedDesc = rawDescForExtract as Record<string, string>;
      const descKey = getDescriptionTranslationKey(item);
      const sourceSnapshot =
        getDescriptionSourceText(item.element) || localizedDesc.default || localizedDesc.en || "";
      if (sourceSnapshot.trim()) {
        for (const lang of languages) {
          if (lang === "en") continue;
          const value = localizedDesc[lang];
          if (!value) continue;
          if (!result[lang]) result[lang] = {};
          result[lang][descKey] = { value, sourceSnapshot: sourceSnapshot.trim() };
        }
      }
    }

    for (const field of getTranslatableLabelFields(item)) {
      const rawLabel = rawEl[field.key];
      if (!rawLabel || typeof rawLabel !== "object") continue;
      const localizedLabel = rawLabel as Record<string, string>;
      const labelKey = getLabelFieldTranslationKey(item, field);
      const sourceSnapshot = getLabelFieldSourceText(item.element, field);
      for (const lang of languages) {
        if (lang === "en") continue;
        const value = localizedLabel[lang];
        if (!value) continue;
        if (!result[lang]) result[lang] = {};
        result[lang][labelKey] = { value, sourceSnapshot };
      }
    }

    const rawTitle = rawEl.title;
    if (rawTitle && typeof rawTitle === "object") {
      const localized = rawTitle as Record<string, string>;
      for (const lang of languages) {
        if (lang === "en") continue;
        const value = localized[lang];
        if (!value) continue;
        if (!result[lang]) result[lang] = {};
        result[lang][translationKey] = { value, sourceSnapshot: sourceText };
      }
    }

    const rawDesc = rawEl.description;
    if (rawDesc && typeof rawDesc === "object") {
      const localizedDesc = rawDesc as Record<string, string>;
      const descKey = getDescriptionTranslationKey(item);
      const descSource =
        localizedDesc.default ??
        localizedDesc.en ??
        getLocalizedTextDefault(localizedDesc as LocalizedQuestionText);
      for (const lang of languages) {
        if (lang === "en") continue;
        const value = localizedDesc[lang];
        if (!value) continue;
        if (!result[lang]) result[lang] = {};
        result[lang][descKey] = { value, sourceSnapshot: descSource };
      }
    }

    // Extract choice translations
    if (Array.isArray(rawEl.choices)) {
      rawEl.choices.forEach((rawChoice, choiceIndex) => {
        if (!rawChoice || typeof rawChoice !== "object") return;
        const choiceObj = rawChoice as { value?: string; text?: unknown };
        if (!choiceObj.text || typeof choiceObj.text !== "object") return;
        const localizedChoiceText = choiceObj.text as Record<string, string>;
        const choiceKey = getChoiceTranslationKey(item, choiceIndex);
        const choiceSourceText =
          localizedChoiceText.default ??
          (typeof choiceObj.value === "string" ? choiceObj.value : "");
        for (const lang of languages) {
          if (lang === "en") continue;
          const value = localizedChoiceText[lang];
          if (!value) continue;
          if (!result[lang]) result[lang] = {};
          result[lang][choiceKey] = { value, sourceSnapshot: choiceSourceText };
        }
      });
    }

    if (Array.isArray(rawEl.rangeLabels)) {
      for (const rawRl of rawEl.rangeLabels) {
        if (!rawRl || typeof rawRl !== "object") continue;
        const rlObj = rawRl as { id?: string; label?: unknown };
        if (typeof rlObj.id !== "string" || !rlObj.label || typeof rlObj.label !== "object") {
          continue;
        }
        const localizedRl = rlObj.label as Record<string, string>;
        const rlKey = getRangeLabelTranslationKey(item, rlObj.id);
        const sourceSnapshot = localizedRl.default ?? localizedRl.en ?? "";
        for (const lang of languages) {
          if (lang === "en") continue;
          const value = localizedRl[lang];
          if (!value) continue;
          if (!result[lang]) result[lang] = {};
          result[lang][rlKey] = { value, sourceSnapshot };
        }
      }
    }
  }

  return result;
}

/** Non-empty target-language strings for one translation key (cleared locales are omitted). */
function collectTranslationStringsForKey(
  translationsByLanguage: Record<string, Record<string, { value: string; sourceSnapshot: string }>>,
  translatableLanguages: readonly string[],
  translationKey: string,
): Record<string, string> {
  const langs: Record<string, string> = {};
  for (const lang of translatableLanguages) {
    const entry = translationsByLanguage[lang]?.[translationKey];
    if (entry?.value.trim()) langs[lang] = entry.value;
  }
  return langs;
}

export function mergeTranslationsIntoDefinition(
  definition: SurveyDefinition,
  translationsByLanguage: Record<string, Record<string, { value: string; sourceSnapshot: string }>>,
  flattenedItems: FlattenedSurveyItem[],
  translatableLanguages: readonly string[],
): SurveyDefinition {
  let result = definition;
  for (const item of flattenedItems) {
    // A divider has no translatable content; leave its `<hr />` html untouched
    // (never localize/wrap it) and skip it entirely.
    if (item.kind === "divider") continue;

    const key = getTranslationItemKey(item);
    const langs = collectTranslationStringsForKey(
      translationsByLanguage,
      translatableLanguages,
      key,
    );

    if (item.element.type === "html") {
      const defaultHtml = (item.element.html as string) ?? "";
      const tag = item.kind === "header" ? parseHeaderTagFromHtml(defaultHtml) : null;
      const wrapHtml = (text: string) =>
        tag ? `<${tag}>${escapeHtml(text)}</${tag}>` : `<p>${escapeHtml(text)}</p>`;
      const localizedHtml: Record<string, string> = {
        default: defaultHtml,
        ...Object.fromEntries(Object.entries(langs).map(([lang, text]) => [lang, wrapHtml(text)])),
      };
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        html: localizedHtml,
      }));
    } else {
      const sourceText = getItemSourceText(item);
      const localizedTitle: Record<string, string> = { default: sourceText, ...langs };
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        title: localizedTitle,
      }));
    }

    if (hasTranslatableQuestionDescription(item)) {
      const descKey = getDescriptionTranslationKey(item);
      const descLangs = collectTranslationStringsForKey(
        translationsByLanguage,
        translatableLanguages,
        descKey,
      );
      const sourceText = getDescriptionSourceText(item.element);
      const localizedDescription: LocalizedQuestionText = {
        default: sourceText,
        ...descLangs,
      };
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        description: localizedDescription,
      }));
    }

    for (const field of getTranslatableLabelFields(item)) {
      const labelLangs = collectTranslationStringsForKey(
        translationsByLanguage,
        translatableLanguages,
        getLabelFieldTranslationKey(item, field),
      );
      const localizedLabel: LocalizedQuestionText = {
        default: getLabelFieldSourceText(item.element, field),
        ...labelLangs,
      };
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        [field.key]: localizedLabel,
      }));
    }

    if (hasTranslatableChoices(item.element)) {
      const choices = item.element.choices!;
      const newChoices = choices.map((choice, choiceIdx) => {
        const choiceKey = getChoiceTranslationKey(item, choiceIdx);
        const choiceSourceText = getChoiceSourceText(choice);
        const choiceValue = getChoiceValue(choice);
        const choiceLangs = collectTranslationStringsForKey(
          translationsByLanguage,
          translatableLanguages,
          choiceKey,
        );
        return {
          value: choiceValue,
          text: { default: choiceSourceText, ...choiceLangs },
        };
      });
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        choices: newChoices,
      }));
    }

    if (hasTranslatableRangeLabels(item.element)) {
      const labels = [...item.element.rangeLabels!].sort((a, b) => a.order - b.order);
      const newRangeLabels = labels.map(rl => {
        const rlKey = getRangeLabelTranslationKey(item, rl.id);
        const sourceText = getRangeLabelSourceText(rl);
        const rlLangs = collectTranslationStringsForKey(
          translationsByLanguage,
          translatableLanguages,
          rlKey,
        );
        return {
          ...rl,
          label: { default: sourceText, ...rlLangs },
        };
      });
      result = updateElementAtOrder(result, item.order, el => ({
        ...el,
        rangeLabels: newRangeLabels,
      }));
    }
  }
  return result;
}

export function moveElementToIndex(
  definition: SurveyDefinition,
  sourceOrder: number,
  destinationOrder: number,
): SurveyDefinition {
  if (sourceOrder === destinationOrder) {
    return definition;
  }

  const flat = flattenQuestions(definition);
  const source = flat[sourceOrder];
  const destination = flat[destinationOrder];

  if (!source || !destination) {
    return definition;
  }

  const pages = definition.pages.map(page => ({ ...page, elements: [...page.elements] }));
  const [movingElement] = pages[source.pageIndex].elements.splice(source.elementIndex, 1);

  const adjustedDestinationIndex =
    source.pageIndex === destination.pageIndex && source.elementIndex < destination.elementIndex
      ? destination.elementIndex - 1
      : destination.elementIndex;

  pages[destination.pageIndex].elements.splice(adjustedDestinationIndex, 0, movingElement);
  return { pages: pages.filter(page => page.elements.length > 0) };
}

/** Avoids editor flicker when syncing translation fields from store with unchanged values. */
export function areStringRecordsEqual(
  a: Record<string, string>,
  b: Record<string, string>,
  keys: readonly string[],
): boolean {
  return keys.every(key => (a[key] ?? "") === (b[key] ?? ""));
}

export function areNestedNumberKeyedRecordsEqual(
  a: Record<string, Record<number, string>>,
  b: Record<string, Record<number, string>>,
  languages: readonly string[],
): boolean {
  for (const lang of languages) {
    const aLang = a[lang] ?? {};
    const bLang = b[lang] ?? {};
    const indices = new Set([...Object.keys(aLang).map(Number), ...Object.keys(bLang).map(Number)]);
    for (const idx of indices) {
      if ((aLang[idx] ?? "") !== (bLang[idx] ?? "")) {
        return false;
      }
    }
  }
  return true;
}

export function areNestedStringKeyedRecordsEqual(
  a: Record<string, Record<string, string>>,
  b: Record<string, Record<string, string>>,
  languages: readonly string[],
): boolean {
  for (const lang of languages) {
    const aLang = a[lang] ?? {};
    const bLang = b[lang] ?? {};
    const ids = new Set([...Object.keys(aLang), ...Object.keys(bLang)]);
    for (const id of ids) {
      if ((aLang[id] ?? "") !== (bLang[id] ?? "")) {
        return false;
      }
    }
  }
  return true;
}
